import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../config';

const MONTH_OPTIONS = [
  { value: 6, label: '6+ months (sale se, −25%)' },
  { value: 9, label: '9+ months (sale se, −30%)' },
  { value: 12, label: '12+ months (sale se, −40%)' }
];

function sampleMessage(row) {
  const name = (row.customerName || 'Customer').split(' ')[0];
  const age = row.ageLabel || `${row.ageMonths} महीने`;
  if (row.ageMonths >= 12 || row.templateType === 'buyback_revisit3') {
    return `नमस्ते ${name} जी! 🙏

आपने *Baghel Telecom* से *${row.productName}* खरीदा था। समय के साथ यह फ़ोन पुराना हो गया है, लेकिन आज भी इसकी अच्छी कीमत मिल सकती है।

📱 अनुमानित एक्सचेंज/बायबैक मूल्य: *~₹${row.estimatedValue?.toLocaleString('en-IN')}*
(वास्तविक मूल्य स्टोर पर बताया जाएगा)

🔄 पुराना फ़ोन बेचकर *नए प्रीमियम फ़ोन* में अपग्रेड करें — A2ZTak पर सर्वोत्तम सौदा!

*BECHO* लिखकर जवाब दें — या सीधे स्टोर आएँ।`;
  }
  return `नमस्ते ${name} जी! 🙏

आपका *${row.productName}* अब *${age}* से आपके पास है — फ़ोन पुराना हो गया है, मगर आज भी *~₹${row.estimatedValue?.toLocaleString('en-IN')}* तक अच्छी कीमत मिल सकती है।

पुराना फ़ोन बेचकर *नए प्रीमियम फ़ोन* में अपग्रेड करें — A2ZTak पर सर्वोत्तम सौदा!

*BECHO* लिखकर जवाब दें — या स्टोर आएँ।`;
}

function BuybackCampaign() {
  const API = config.API_URL;
  const [months, setMonths] = useState(6);
  const [productFilter, setProductFilter] = useState('');
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [dailyRemaining, setDailyRemaining] = useState(50);
  const [todaySent, setTodaySent] = useState(0);
  const [excludedAlreadyMessaged, setExcludedAlreadyMessaged] = useState(0);
  const [history, setHistory] = useState([]);

  const loadProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buyback-campaign/products?branchId=PB001`);
      setProducts(res.data.products || []);
    } catch (e) {
      console.error(e);
    }
  }, [API]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buyback-campaign/history?limit=15`);
      setHistory(res.data.campaigns || []);
    } catch (e) {
      console.error(e);
    }
  }, [API]);

  const loadEligible = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const params = new URLSearchParams({
        branchId: 'PB001',
        months: String(months),
        product: productFilter
      });
      const res = await axios.get(`${API}/buyback-campaign/eligible-customers?${params}`);
      const list = res.data.rows || [];
      setRows(list);
      setDailyRemaining(res.data.dailyRemaining ?? 50);
      setTodaySent(res.data.todaySent ?? 0);
      setExcludedAlreadyMessaged(res.data.excludedAlreadyMessaged ?? 0);
      const allSelected = {};
      list.forEach((r) => { allSelected[r.emiId] = true; });
      setSelected(allSelected);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, months, productFilter]);

  useEffect(() => {
    loadProducts();
    loadHistory();
  }, [loadProducts, loadHistory]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected[r.emiId]),
    [rows, selected]
  );

  const sendCount = selectedRows.length;
  const previewSample = selectedRows[0] || rows[0] || null;

  const toggleAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const next = {};
    rows.forEach((r) => { next[r.emiId] = true; });
    setSelected(next);
  };

  const toggleRow = (emiId) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[emiId]) delete next[emiId];
      else next[emiId] = true;
      return next;
    });
  };

  const removeFromPreview = (emiId) => {
    setRows((prev) => prev.filter((r) => r.emiId !== emiId));
    setSelected((prev) => {
      const next = { ...prev };
      delete next[emiId];
      return next;
    });
  };

  const handleSend = async () => {
    if (!sendCount) {
      setError('Kam se kam 1 customer select karein ya preview se hataayein.');
      return;
    }
    const toSend = Math.min(sendCount, dailyRemaining);
    const names = selectedRows.slice(0, 3).map((r) => r.customerName).join(', ');
    const more = sendCount > 3 ? ` +${sendCount - 3} aur` : '';
    if (!window.confirm(`${toSend} customers ko message jayega:\n${names}${more}\n\nConfirm karein?`)) return;

    setSending(true);
    setError('');
    setMessage('');
    try {
      const customerIds = selectedRows.map((r) => r.emiId);
      const res = await axios.post(`${API}/buyback-campaign/send`, {
        branchId: 'PB001',
        months,
        productFilter,
        customerIds,
        campaignName: `Buyback ${months}mo ${productFilter || 'All'}`
      });
      if (res.data.success) {
        setMessage(res.data.message || 'Campaign bhej di.');
      } else {
        setError(res.data.message || 'Send fail.');
      }
      await loadEligible();
      await loadHistory();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="buyback-campaign-page">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div>
          <h2 className="mb-1"><i className="bi bi-arrow-repeat me-2"></i>Buyback Revisit Campaign</h2>
          <p className="text-muted mb-0">
            Pehle preview dekhein, jo nahi bhejna unhe list se hata dein. Reply par AI smart jawab + Phone Becho.
          </p>
        </div>
        <div className="text-end">
          <span className="badge bg-info text-dark me-2">Aaj bheje: {todaySent}/50</span>
          <span className="badge bg-success">Baki quota: {dailyRemaining}</span>
        </div>
      </div>

      <div className="alert alert-success py-2">
        <i className="bi bi-check-circle me-1"></i>
        Templates: <strong>revisit2</strong> (12 mahine se kam, age label ke sath) · <strong>revisit3</strong> (12+ mahine)
        <br />
        <span className="small">Umar <strong>sale date</strong> se count hoti hai (EMI complete date se nahi). Message ke <strong>30 din</strong> baad customer dubara list me aa sakta hai — buyback ke liye reminder bhej sakte ho.</span>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">EMI age (completed)</label>
              <select className="form-select" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
                {MONTH_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label">Product filter (optional)</label>
              <input
                className="form-control"
                list="buyback-products"
                placeholder="e.g. Poco C75 5G"
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
              />
              <datalist id="buyback-products">
                {products.map((p) => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div className="col-md-4">
              <button type="button" className="btn btn-primary w-100" onClick={loadEligible} disabled={loading}>
                {loading ? 'Loading…' : 'Preview — kaun kaun ko jayega'}
              </button>
            </div>
          </div>
          {excludedAlreadyMessaged > 0 && (
            <p className="text-muted small mt-2 mb-0">
              <i className="bi bi-check-circle text-success me-1"></i>
              {excludedAlreadyMessaged} customer last 30 din me message mil chuka hai — isliye abhi list me nahi dikh rahe.
            </p>
          )}
        </div>
      </div>

      {previewSample && (
        <div className="card mb-4 border-success">
          <div className="card-header bg-success bg-opacity-10">
            <i className="bi bi-chat-dots me-1"></i>
            Message preview ({sendCount} customer{sendCount !== 1 ? 's' : ''} ko jayega)
          </div>
          <div className="card-body">
            <p className="mb-1 small text-muted">Example — {previewSample.customerName}:</p>
            <div className="p-3 bg-light rounded border">{sampleMessage(previewSample)}</div>
            <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSend}
                disabled={sending || sendCount === 0 || dailyRemaining <= 0}
              >
                {sending ? 'Sending…' : `Confirm & Send (${Math.min(sendCount, dailyRemaining)})`}
              </button>
              <span className="text-muted small">
                Uncheck ya Remove se list se hata sakte hain — bhejne se pehle review karein
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            Preview list — message jayega in {sendCount} / {rows.length} ko
          </span>
          {rows.length > 0 && (
            <label className="mb-0 small">
              <input
                type="checkbox"
                className="form-check-input me-1"
                checked={sendCount === rows.length && rows.length > 0}
                onChange={(e) => toggleAll(e.target.checked)}
              />
              Sab select
            </label>
          )}
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Send?</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Product</th>
                <th>Sale ₹</th>
                <th>Age (sale)</th>
                <th>Template</th>
                <th>Est. value</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="text-center text-muted py-4">Filter set karke Preview dabayein</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.emiId} className={selected[r.emiId] ? '' : 'table-secondary opacity-75'}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!selected[r.emiId]}
                      onChange={() => toggleRow(r.emiId)}
                    />
                  </td>
                  <td>{r.customerName}</td>
                  <td>{r.customerPhone}</td>
                  <td>{r.productName}</td>
                  <td>₹{r.salePrice?.toLocaleString('en-IN')}</td>
                  <td>{r.ageLabel || `${r.ageMonths} mo`}</td>
                  <td><span className="badge bg-secondary">{r.templateType === 'buyback_revisit3' ? '12+' : 'revisit2'}</span></td>
                  <td className="fw-semibold text-success">~₹{r.estimatedValue?.toLocaleString('en-IN')}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title="List se hatao"
                      onClick={() => removeFromPreview(r.emiId)}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span><i className="bi bi-bar-chart me-1"></i>Campaign analysis</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadHistory}>
            Refresh
          </button>
        </div>
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Campaign</th>
                <th>Filter</th>
                <th>Sent</th>
                <th>Failed</th>
                <th>Replied</th>
                <th>Reply %</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={7} className="text-muted text-center py-3">Abhi koi campaign nahi</td></tr>
              )}
              {history.map((c) => (
                <tr key={c._id}>
                  <td>{new Date(c.createdAt).toLocaleDateString('en-IN')}</td>
                  <td>{c.name}</td>
                  <td>{c.filterMonths}+ mo {c.productFilter ? `· ${c.productFilter}` : ''}</td>
                  <td>{c.stats?.sent ?? 0}</td>
                  <td>{c.stats?.failed ?? 0}</td>
                  <td>{c.stats?.replied ?? 0}</td>
                  <td>
                    <span className={`badge ${c.replyRate >= 20 ? 'bg-success' : 'bg-secondary'}`}>
                      {c.replyRate ?? 0}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default BuybackCampaign;
